import type {BusinessFee} from '@/types';
import {
  AfriexCountryCode,
  AfriexCurrencyCode,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  AfriexTransactionType,
  AfriexWalletType,
  BusinessFeeAction,
  BusinessFeeComparator,
  BusinessFeeField,
} from '@/types';
import {isolateErrorSync, isValidPositiveNumber} from '@/utils/dashboard';

import {ValidIsoDateTime, ValidUserID} from './createValidator';

const OPERATOR_DELIMITER =
  /_EQUALS_|_NOT_EQUALS_|_BETWEEN_|_GREATER_THAN_|_LESS_THAN_/;

export class BusinessFeeValidator {
  constructor(private request: Partial<BusinessFee>) {}

  private throwErrorMessage(errorMessage: string): never {
    throw new Error(errorMessage);
  }

  private validateName() {
    if (!this.request?.name) {
      this.throwErrorMessage('No fee name provided');
    }
    return this;
  }

  private validateAmount() {
    // Zero is a valid fee amount: a zero fee is the signal to defer pricing to
    // the next-best matching (default) fee at selection time (see
    // BusinessFeeEntity). Only a missing amount — neither a fixed nor a
    // percent value provided — is rejected here. Keep in sync with the
    // server-side BusinessFeeValidator.
    const hasFixedFee =
      this.request?.amount?.valueFixed !== undefined &&
      this.request?.amount?.valueFixed !== '';
    const hasPercentFee =
      this.request?.amount?.valuePercent !== undefined &&
      this.request?.amount?.valuePercent !== null;
    if (!hasFixedFee && !hasPercentFee) {
      this.throwErrorMessage('No fee amount provided');
    }
    if (
      this.request?.amount?.valueFixed &&
      !this.request?.amount?.currencyCode
    ) {
      this.throwErrorMessage('No currency code provided for fee amount');
    }
    if (
      this.request?.amount?.valueFixed &&
      this.request?.amount?.currencyCode &&
      !Object.values(AfriexCurrencyCode).includes(
        this.request?.amount?.currencyCode,
      )
    ) {
      this.throwErrorMessage('Invalid currency code provided for fee amount');
    }

    if (
      this.request?.amount?.valueFixed &&
      !isValidPositiveNumber(this.request?.amount?.valueFixed)
    ) {
      this.throwErrorMessage('Invalid fixed fee amount provided');
    }

    if (
      this.request?.amount?.valueMax &&
      !isValidPositiveNumber(this.request?.amount?.valueMax)
    ) {
      this.throwErrorMessage('Invalid max fee amount provided');
    }
    if (
      this.request?.amount?.valuePercent &&
      (!isValidPositiveNumber(this.request?.amount?.valuePercent) ||
        Number(this.request?.amount?.valuePercent) > 100)
    ) {
      this.throwErrorMessage('Invalid fixed fee percentage provided');
    }
  }

  private validateAction() {
    if (!this.request?.action) {
      this.throwErrorMessage('No fee application strategy provided');
    }
    if (!Object.values(BusinessFeeAction).includes(this.request?.action)) {
      this.throwErrorMessage('Invalid fee application strategy provided');
    }
  }

  private validateCurrencyTag(tag: string) {
    const [, raw] = tag.split(OPERATOR_DELIMITER);
    const values = raw.split(',').map((value) => value.trim());
    values.forEach((currencyCode) => {
      if (
        !Object.values(AfriexCurrencyCode).includes(
          currencyCode as AfriexCurrencyCode,
        )
      ) {
        this.throwErrorMessage(
          `Invalid currency code provided for fee filter: ${tag}`,
        );
      }
    });
  }

  private validateChannelTag(tag: string) {
    const [, raw] = tag.split(OPERATOR_DELIMITER);
    const values = raw.split(',').map((value) => value.trim());
    values.forEach((channel) => {
      if (
        !Object.values(AfriexTransactionChannel).includes(
          channel as AfriexTransactionChannel,
        )
      ) {
        this.throwErrorMessage(
          `Invalid transaction channel provided for fee filter: ${tag}`,
        );
      }
    });
  }

  private validateProcessorTag(tag: string) {
    const [, raw] = tag.split(OPERATOR_DELIMITER);
    const values = raw.split(',').map((value) => value.trim());
    values.forEach((processor) => {
      if (
        !Object.values(AfriexTransactionProcessor).includes(
          processor as AfriexTransactionProcessor,
        )
      ) {
        this.throwErrorMessage(
          `Invalid transaction processor provided for fee filter: ${tag}`,
        );
      }
    });
  }

  private validateTransactionTypeTag(tag: string) {
    const [, raw] = tag.split(OPERATOR_DELIMITER);
    const values = raw.split(',').map((value) => value.trim());
    values.forEach((type) => {
      if (
        !Object.values(AfriexTransactionType).includes(
          type as AfriexTransactionType,
        )
      ) {
        this.throwErrorMessage(
          `Invalid transaction type provided for fee filter: ${tag}`,
        );
      }
    });
  }

  private validateTransactionDateTag(tag: string) {
    const [, raw] = tag.split(OPERATOR_DELIMITER);
    const values = raw.split(',').map((value) => value.trim());
    values.forEach((value) => {
      const [, validationError] = isolateErrorSync(() =>
        ValidIsoDateTime.validate(value),
      );
      if (validationError) {
        this.throwErrorMessage(
          `Invalid transaction date provided for fee filter: ${tag}`,
        );
      }
    });
  }

  private validateUserIdTag(tag: string) {
    const [, user] = tag.split(OPERATOR_DELIMITER);
    const values = user.split(',').map((value) => value.trim());
    values.forEach((value) => {
      const [, validationError] = isolateErrorSync(() =>
        ValidUserID.validate(value),
      );
      if (validationError) {
        this.throwErrorMessage(
          `Invalid user or business id provided for fee filter: ${tag}`,
        );
      }
    });
  }

  private validateCountryTag(tag: string) {
    const [, raw] = tag.split(OPERATOR_DELIMITER);
    const values = raw.split(',').map((value) => value.trim());
    values.forEach((countryCode) => {
      if (
        !Object.values(AfriexCountryCode).includes(
          countryCode as AfriexCountryCode,
        )
      ) {
        this.throwErrorMessage(
          `Invalid country code provided for fee filter: ${tag}`,
        );
      }
    });
  }

  private validateWalletTag(tag: string) {
    const [, raw] = tag.split(OPERATOR_DELIMITER);
    const values = raw.split(',').map((value) => value.trim());
    values.forEach((walletTag) => {
      if (
        !Object.values(AfriexWalletType).includes(walletTag as AfriexWalletType)
      ) {
        this.throwErrorMessage(
          `Invalid wallet tag provided for fee filter: ${tag}`,
        );
      }
    });
  }

  validateAmountTag(tag: string) {
    const [, amount] = tag.split(OPERATOR_DELIMITER);
    const values = amount.split(',').map((value) => value.trim());
    values.forEach((value) => {
      if (!isValidPositiveNumber(value)) {
        this.throwErrorMessage(`Invalid value provided for fee filter ${tag}`);
      }
    });
  }

  private validateTagComparator(tag: string) {
    const match = tag.match(
      /_(EQUALS|NOT_EQUALS|GREATER_THAN|LESS_THAN|BETWEEN)_/,
    );
    const comparator = match ? match[1] : null;
    if (
      !Object.values(BusinessFeeComparator).includes(
        comparator as BusinessFeeComparator,
      )
    ) {
      this.throwErrorMessage(
        `Invalid comparator provided for fee filter: ${tag}`,
      );
    }
  }

  private validateTag(tag: string) {
    if (tag.includes('_AMOUNT_')) {
      return this.validateAmountTag(tag);
    }
    if (tag.includes('_COUNTRY_')) {
      return this.validateCountryTag(tag);
    }
    if (
      tag.startsWith('USERID_') ||
      tag.includes('_USERID_') ||
      tag.startsWith('BUSINESSID_') ||
      tag.includes('_BUSINESSID_')
    ) {
      return this.validateUserIdTag(tag);
    }
    if (
      tag.startsWith('TRANSACTION_TYPE_') ||
      tag.includes('_TRANSACTION_TYPE_')
    ) {
      return this.validateTransactionTypeTag(tag);
    }
    if (
      tag.startsWith('TRANSACTION_DATE_') ||
      tag.includes('_TRANSACTION_DATE_')
    ) {
      return this.validateTransactionDateTag(tag);
    }
    if (tag.includes('_PROCESSOR_')) {
      return this.validateProcessorTag(tag);
    }
    if (tag.includes('_CHANNEL_')) {
      return this.validateChannelTag(tag);
    }
    if (tag.includes('_CURRENCY_')) {
      return this.validateCurrencyTag(tag);
    }
    if (tag.startsWith('WALLET_TAG_') || tag.includes('_WALLET_TAG_')) {
      return this.validateWalletTag(tag);
    }
  }

  private validateTags() {
    if (!this.request?.tags?.length) {
      this.throwErrorMessage('No fee filters provided');
    }

    this.request?.tags?.forEach((tag) => {
      this.validateTagComparator(tag);
    });

    const hasDepositTag = this.request?.tags?.some(
      (tag) =>
        tag ===
        `${BusinessFeeField.TransactionType}_${BusinessFeeComparator.Equals}_${AfriexTransactionType.DEPOSIT}`,
    );
    const hasWithdrawalTag = this.request?.tags?.some(
      (tag) =>
        tag ===
        `${BusinessFeeField.TransactionType}_${BusinessFeeComparator.Equals}_${AfriexTransactionType.WITHDRAW}`,
    );
    const hasSwapTag = this.request?.tags?.some(
      (tag) =>
        tag ===
        `${BusinessFeeField.TransactionType}_${BusinessFeeComparator.Equals}_${AfriexTransactionType.SWAP}`,
    );
    if (hasDepositTag && hasWithdrawalTag) {
      this.throwErrorMessage('Conflicting fee filters provided');
    }
    if (!hasDepositTag && !hasWithdrawalTag && !hasSwapTag) {
      this.throwErrorMessage('Transaction type filter must be provided');
    }

    this.request?.tags?.forEach((tag) => {
      this.validateTag(tag);
    });

    return this;
  }

  // A zero fee is a deferral signal ("use the default fee") and only makes
  // sense scoped to a specific user or business — a global zero fee would
  // shadow the default set for everyone. Require a USERID/BUSINESSID owner
  // tag on zero fees. Keep in sync with the server-side BusinessFeeValidator.
  private validateZeroFeeOwnerTag() {
    const isZeroFee =
      !Number(this.request?.amount?.valueFixed ?? 0) &&
      !Number(this.request?.amount?.valuePercent ?? 0);
    if (!isZeroFee) {
      return;
    }
    const ownerTagPattern = new RegExp(
      `^(${BusinessFeeField.UserId}|${BusinessFeeField.BusinessId})_${BusinessFeeComparator.Equals}_`,
    );
    const hasOwnerTag = (this.request?.tags ?? []).some((tag) =>
      ownerTagPattern.test(tag),
    );
    if (!hasOwnerTag) {
      this.throwErrorMessage(
        'Zero fees must be scoped to a user or business (USERID or BUSINESSID tag required)',
      );
    }
  }

  private validate() {
    this.validateAction();
    this.validateAmount();
    this.validateName();
    this.validateTags();
    this.validateZeroFeeOwnerTag();
  }

  value(): BusinessFee {
    this.validate();
    return this.request as BusinessFee;
  }
}
